/**
 * Reine, TESTBARE Ableitung der State-Aktionen der geerbten Detail-Aktions-Leiste
 * (CardDetailActions). Extrahiert 2026-07-14 (P8/#467), damit die Sackgassen-Frei-
 * Invariante als Unit-Test abgesichert ist — der Render in CardDetailActions.tsx
 * nutzt exakt diese Flags.
 *
 * P8-INVARIANTE (Sackgassen-Frei): für jeden erreichbaren Karten-State gibt es
 * mindestens einen NICHT-Reclassify-Weg (vorwärts oder zurück/neu einlesen). Vorher
 * waren read-only Views (Zulagenblatt/Excel) ab `ocr_complete`/`pending_review`
 * Sackgassen (nur reclassify), weil „Validieren" view-eigen ist und `start-ocr` nur
 * am Board existierte.
 *
 * Server-Guards, an die die Sichtbarkeit gekoppelt ist (Prinzip „nie einen Knopf
 * zeigen, den der Server ablehnt"):
 *  - start-ocr: {uploaded, ocr_pending, ocr_complete, pending_review}
 *  - validate:  timesheet {uploaded,ocr_pending,ocr_complete,pending_conflict},
 *               expense {ocr_pending,ocr_complete,pending_conflict}
 *               → kind-gemeinsame, sinnvolle Schnittmenge: {ocr_complete, pending_conflict}
 *  - reclassify blockiert in: calculated, validated, exported, archived, superseded
 */
export const RECLASSIFY_BLOCKED = new Set([
  "calculated", "validated", "exported", "archived", "superseded",
]);

export type CardActionFlags = {
  reopen: boolean;
  exportCard: boolean;
  reExport: boolean;
  revert: boolean;
  neuEinlesen: boolean;
  uebernehmen: boolean;
  reclassify: boolean;
};

export function cardActionFlags(
  state: string,
  opts: { allowInlineValidate?: boolean } = {},
): CardActionFlags {
  const isCalc = state === "calculated";
  return {
    reopen: isCalc || state === "validated",
    exportCard: isCalc,
    reExport: state === "exported",
    revert: isCalc || state === "exported",
    // „Neu einlesen (OCR)" = start-ocr — nur wo die Karte schon gelesen ist
    // (Sackgassen-Fix; start-ocr existierte bisher nur am Board):
    neuEinlesen: state === "ocr_complete" || state === "pending_review",
    // „Übernehmen (berechnen)" = validate — nur read-only Views (editierbare
    // Views haben ein eigenes Validieren mit Quality-Gate):
    uebernehmen: !!opts.allowInlineValidate && (state === "ocr_complete" || state === "pending_conflict"),
    reclassify: !RECLASSIFY_BLOCKED.has(state),
  };
}

/** Server-Fehlertext MENSCHLICH machen (Anlassfall 4e2166f7, Thomas 23.07.:
 *  rohes `{"error":"internal_error"}` stand in der UI). JSON-Bodies liefern
 *  `message` (sprechend, deutsch) oder `error` (Code) — sonst Rohtext. */
export function humanServerError(raw: string): string {
  try {
    const b = JSON.parse(raw);
    if (typeof b?.message === "string" && b.message) return b.message;
    if (typeof b?.error === "string" && b.error) return b.error;
  } catch { /* kein JSON — Rohtext zeigen */ }
  return raw;
}

/** Nach einem Re-OCR-Anstoß auf das Ende der Lesung warten (Anlassfall Thomas
 *  23.07.: Status blieb auf `ocr_pending` stehen, bis manuell refresht wurde).
 *  Pollt den Karten-State alle 3 s bis er ocr_pending/ocr_running verlässt
 *  (max. `maxMs`), damit der Aufrufer danach EINMAL frisch laden kann. */
export async function waitForCardRead(
  kind: "timesheet" | "expense",
  cardId: string,
  maxMs = 150_000,
): Promise<void> {
  const path = kind === "timesheet" ? "timesheets" : "expenses";
  const t0 = Date.now();
  while (Date.now() - t0 < maxMs) {
    await new Promise((r) => setTimeout(r, 3_000));
    try {
      const rr = await fetch(new URL(`api/${path}/${cardId}`, document.baseURI).toString(), {
        credentials: "same-origin",
      });
      if (!rr.ok) return;
      const body = await rr.json();
      const st = (body.timesheet ?? body.expense)?.state as string | undefined;
      if (st && st !== "ocr_pending" && st !== "ocr_running") return;
    } catch {
      return;
    }
  }
}

/** Eine Dokumentart des Mandanten, wie sie die Typ-Registry liefert. */
export type MandantTyp = { id: string; label: string; routesTo: "timesheet" | "expense" };

/** Eine Wahlmöglichkeit im gelben Kasten „Diese Seite wurde noch nicht
 *  klassifiziert — bitte entscheide". `docType` fehlt nur beim Bestandsmandanten
 *  (keine Registry) — dort leitet der Server die Art aus der Route ab. */
export type KlassifikationsOption = {
  label: string;
  route: "timesheet" | "expense";
  docType?: string;
};

/**
 * WAS EINE UNKLARE SEITE ZUR WAHL STELLT (Thomas 14.08.2026).
 *
 * Regel: die Arten, die DIESER MANDANT hat — nicht die des Bestandskunden.
 * Gemessen am 14.08. auf prod: einer 3G-Seite wurden „Als Stundenliste
 * verarbeiten" und „Als Fahrtenbuch (km-Geld) verarbeiten" angeboten. 3G hat
 * keine Stundenliste; seine beiden Blattarten („Zulagenblatt", „Geleistete
 * Stunden für Zusätzliche Projekte") standen gar nicht zur Wahl. Der Kanban
 * machte es schon richtig — dieselbe Frage, zwei Antworten.
 *
 * Jede Option trägt ihren `docType` mit. Ohne ihn leitet der Server die Art aus
 * der Route ab, was nur bei genau EINER Art je Route aufgeht; seit
 * `projektstunden` (Karte 721) hat 3G zwei auf der timesheet-Route und der
 * Aufruf endet in `doc_type_required`.
 *
 * Ohne Registry (Bestandsmandant): unverändert zwei Optionen ohne `docType`.
 */
export function klassifikationsOptionen(
  mandantTypen: ReadonlyArray<MandantTyp>,
  fallback: { timesheet: string; expense: string },
): KlassifikationsOption[] {
  if (mandantTypen.length > 0) {
    return mandantTypen.map((t) => ({ label: t.label, route: t.routesTo, docType: t.id }));
  }
  return [
    { label: fallback.timesheet, route: "timesheet" },
    { label: fallback.expense, route: "expense" },
  ];
}
